import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { catchError } from "rxjs";
import { ToastService } from "../services/toast.service";
import { SnackbarService } from "../services/snackbar.service";
import { AuthService } from "../services/auth.service";
import { ErrorUtils } from "../utils/error-utils";
import { DialogService } from "../services/dialog.service";
import { Router } from "@angular/router";
import { NotificationData } from "shared/models/notification-data";
import { ObjectUtils } from "shared/utils/object.utils";
import { Utils } from "shared/utils/utils";
import { SubscriptionUpgradeAlertDialogComponent } from "shared/dialogs/subscription-upgrade-alert-dialog/subscription-upgrade-alert-dialog.component";
import { MatDialog } from "@angular/material/dialog";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  private readonly _toastService: ToastService = inject(ToastService)
  private readonly _snackbarService: SnackbarService = inject(SnackbarService)
  private readonly _authService: AuthService = inject(AuthService)
  private readonly _router: Router = inject(Router)
  private readonly _dialogService: DialogService = inject(DialogService)
  private readonly _dialog: MatDialog = inject(MatDialog)


  intercept(request: HttpRequest<any>, next: HttpHandler | any): any {
    return next.handle(request)
      .pipe(
        catchError((response: HttpErrorResponse | any) => {

          // Server is not responding
          if (response.status === 0) {
            this._snackbarService.showSnackBar('Could not connect to the server.', "warning", 4000)
          }

          // Unhandled error
          else if (response.status === 500) {
            this._toastService.showToast(
              new NotificationData({
                title: 'An error occurred.',
                message: 'Your request could not be processed correctly. Please report the problem to an administrator.',
                status: 'danger',
              })
            )
          }

          // Unauthorized
          else if (response.status === 401) {

            // If the error is not TOKEN_EXPIRED, it means the user is unauthorized for other reasons (e.g., invalid credentials)
            if (response?.error?.message !== 'TOKEN_EXPIRED') {
              this._authService.clearSession()
              this._snackbarService.showSnackBar('You have been logged out. Your session has expired.', 'info')
              this._router.navigate(['login']);
            }
          }

          // Forbidden
          else if (response.status === 403) {

            // Must propagate error to successfully logout
            if (response?.url?.includes('logout')) {
              throw new HttpErrorResponse(response);
            }

            const errorMessage = response?.error?.message || '';

            // Permission denied — show snackbar, don't logout
            if (errorMessage.startsWith('Forbidden:')) {
              this._snackbarService.showSnackBar('Permission denied', 'error', 3000);
              throw new HttpErrorResponse(response);
            }

            this._authService.clearSession()
            this._snackbarService.showSnackBar('You have been logged out. Your session has expired.', 'info')
            this._router.navigate(['login']);

          }

          else if (response.status === 422) {

            const errorMessage = ObjectUtils.getNestedValue(response, "error.message")

            // Limit excedeed
            if (errorMessage == 'LIMIT_EXCEEDED') {
              this._dialog.open(SubscriptionUpgradeAlertDialogComponent, {
                data: {
                  reason: errorMessage
                },
                maxWidth: '100%'
              });
              throw new HttpErrorResponse(response);
            }

            const errors: any = ErrorUtils.extractErrors(response.error)

            this._dialogService.showFormErrorsDialog(errors)
            this._snackbarService.showSnackBar('Please correct the errors in the form.',
              'warning',
              4000
            )
          }

          // Synchronization needs error handling
          throw new HttpErrorResponse(response);
        })
      )
  }
}


export const ErrorInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ErrorInterceptor,
  multi: true
}




